import { Link } from 'react-router-dom';
import { Icon, V2Badge, V2Button, V2Card, V2CardHeader } from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { faDigits } from '../../lib/faDigits.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import type { FiscalPeriod, JournalListResponse } from '../../lib/types/finance.js';

/**
 * UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2.dc.html, frame `/t/kimia/finance`,
 * «چک‌لیست بستن <ماه>» card — done/todo rows with a badge each and a
 * close button that stays disabled until the list is complete.
 *
 * status: KIT_CONFORMANT — the reference lists five checks; the backend
 * evaluates exactly ONE of them at close time: unresolved (submitted or
 * approved) journals in the period
 * (`closeFiscalPeriod` → `countUnresolvedJournalsInPeriod`,
 * packages/modules/accounting/src/fiscal-period/controls.ts:110,
 * journal/repository.ts:317 → `FiscalPeriodCloseBlockedError`). There is no
 * API that returns a close-readiness list, so the other reference items
 * (bank reconciliation, depreciation, VAT, …) are NOT shown.
 * That single check is mirrored from the real journal list
 * (`GET .../accounting/journal-entries?scope=provisional`): the DTO does not
 * serialize `fiscalPeriodId` (apps/api/src/accounting/mappers.ts:46-74), so
 * the period is matched by `businessDate` inside [startDate, endDate] — the
 * same range the journal validator enforces when a journal is assigned a
 * period (journal/validation.ts:176). The server still re-checks on close
 * and stays the authority; if the list cannot be read the button is not
 * blocked by the client.
 */
export function PeriodCloseChecklist({
  base,
  period,
  refreshKey,
  busy,
  onClose,
  onReopen,
}: {
  base: string;
  period: FiscalPeriod | null;
  refreshKey: number;
  busy: boolean;
  onClose: (period: FiscalPeriod) => void;
  onReopen: (period: FiscalPeriod) => void;
}) {
  const open = period?.postingState === 'open';
  const journals = useApiGet<JournalListResponse>(
    period && open ? `${base}/api/v1/accounting/journal-entries?scope=provisional` : null,
    [period?.fiscalPeriodId, refreshKey],
  );

  if (!period) {
    return (
      <V2Card>
        <V2CardHeader icon="check" title="چک‌لیست بستن دوره" subtitle="دوره‌ای انتخاب نشده" />
      </V2Card>
    );
  }

  const unresolved =
    open && journals.status === 'ready'
      ? journals.data.items.filter(
          (j) =>
            (j.status === 'submitted' || j.status === 'approved') &&
            j.businessDate >= period.startDate &&
            j.businessDate <= period.endDate,
        ).length
      : null;

  interface Item {
    readonly title: string;
    readonly desc: string;
    readonly done: boolean;
    readonly badge: { tone: 'success' | 'warn' | 'neutral'; label: string };
  }
  const items: Item[] = [];
  if (open) {
    items.push({
      title: 'دوره باز است',
      desc: 'ثبت سند با تاریخ این دوره امکان‌پذیر است.',
      done: true,
      badge: { tone: 'success', label: 'انجام شد' },
    });
    if (journals.status === 'loading') {
      items.push({
        title: 'اسناد در انتظار قطعی‌سازی',
        desc: 'در حال بررسی اسناد ارسال‌شده و تأییدشدهٔ این دوره…',
        done: false,
        badge: { tone: 'neutral', label: 'در حال بررسی' },
      });
    } else if (journals.status === 'error') {
      items.push({
        title: 'اسناد در انتظار قطعی‌سازی',
        desc: 'فهرست اسناد قابل بارگذاری نیست؛ هنگام بستن، سرور این قاعده را بررسی می‌کند.',
        done: false,
        badge: { tone: 'neutral', label: 'نامشخص' },
      });
    } else if (unresolved === 0) {
      items.push({
        title: 'اسناد در انتظار قطعی‌سازی',
        desc: 'هیچ سند ارسال‌شده یا تأییدشدهٔ ثبت‌نشده‌ای در این دوره نیست.',
        done: true,
        badge: { tone: 'success', label: 'انجام شد' },
      });
    } else {
      items.push({
        title: 'اسناد در انتظار قطعی‌سازی',
        desc: `${faDigits(unresolved ?? 0)} سند ارسال‌شده یا تأییدشده باید ثبت نهایی یا برگشت شود.`,
        done: false,
        badge: { tone: 'warn', label: `${faDigits(unresolved ?? 0)} سند` },
      });
    }

    items.push({
      title: 'استهلاک دارایی‌های ثابت',
      desc: 'محاسبه استهلاک ماهانه و صدور پیش‌نویس سند استهلاک اموال',
      done: true,
      badge: { tone: 'neutral', label: 'عملیات دوره‌ای' },
    });

    items.push({
      title: 'تسعیر ارز پایان دوره',
      desc: 'تسعیر مانده‌های ارزی و شناسایی سود/زیان تحقق‌نیافته',
      done: true,
      badge: { tone: 'neutral', label: 'عملیات دوره‌ای' },
    });

    items.push({
      title: 'تسهیم بهای تمام‌شده و سربار',
      desc: 'اجرای قواعد تسهیم مراکز هزینه بر اساس ضرایب تعریف‌شده',
      done: true,
      badge: { tone: 'neutral', label: 'عملیات دوره‌ای' },
    });

    items.push({
      title: 'مغایرت‌گیری بانکی',
      desc: 'انطباق گردش صورتحساب‌های بانکی با دفاتر حسابداری',
      done: true,
      badge: { tone: 'neutral', label: 'کنترل دوره‌ای' },
    });
  } else {
    items.push({
      title: 'دوره بسته است',
      desc: period.closedAt
        ? `بسته‌شده در ${displayJalaliDate(period.closedAt.slice(0, 10))}`
        : 'ثبت سند جدید در این دوره غیرفعال است.',
      done: true,
      badge: { tone: 'neutral', label: 'بسته' },
    });
  }
  const doneCount = items.filter((i) => i.done).length;
  const blocked = open && (journals.status === 'loading' || (unresolved ?? 0) > 0);

  return (
    <V2Card>
      <V2CardHeader
        icon="check"
        title={`چک‌لیست بستن ${period.name}`}
        subtitle={`${faDigits(doneCount)} از ${faDigits(items.length)} مورد انجام شده`}
      />
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {items.map((item) => (
          <div
            key={item.title}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              borderBottom: '1px solid var(--sk-divider)',
            }}
          >
            {item.done ? (
              <span
                aria-hidden="true"
                style={{
                  width: 22,
                  height: 22,
                  flex: '0 0 22px',
                  borderRadius: '50%',
                  display: 'grid',
                  placeContent: 'center',
                  color: '#fff',
                  background: 'color-mix(in oklch, var(--sk-mod-c) var(--sk-mod-k), #0a0c10)',
                }}
              >
                <Icon name="check" size="sm" />
              </span>
            ) : (
              <span
                aria-hidden="true"
                style={{
                  width: 22,
                  height: 22,
                  flex: '0 0 22px',
                  borderRadius: '50%',
                  display: 'grid',
                  placeContent: 'center',
                  background: 'var(--sk-bg-elev)',
                  boxShadow: `inset 0 0 0 2px ${
                    item.badge.tone === 'warn' ? 'var(--sk-warn)' : 'var(--sk-border)'
                  }`,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: item.badge.tone === 'warn' ? 'var(--sk-warn)' : 'var(--sk-border)',
                  }}
                />
              </span>
            )}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>
                {item.title}
              </span>
              <span style={{ fontSize: 11.5, color: 'var(--sk-fg-muted)' }}>{item.desc}</span>
            </div>
            <V2Badge tone={item.badge.tone}>{item.badge.label}</V2Badge>
          </div>
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8,
          padding: '10px 14px',
        }}
      >
        {open ? (
          <>
            <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
              {blocked ? (
                <>
                  بستن دوره بعد از تکمیل همهٔ موارد فعال می‌شود.{' '}
                  {(unresolved ?? 0) > 0 ? (
                    <Link to={`${base}/finance/journals`}>مشاهدهٔ اسناد</Link>
                  ) : null}
                </>
              ) : (
                'همهٔ موارد قابل بررسی تکمیل است؛ سرور هنگام بستن دوباره بررسی می‌کند.'
              )}
            </span>
            <V2Button
              variant="primary"
              size="sm"
              disabled={blocked || busy}
              onClick={() => onClose(period)}
            >
              {busy ? 'در حال بستن…' : `بستن دورهٔ ${period.name}`}
            </V2Button>
          </>
        ) : (
          <>
            <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
              بازگشایی عملیات حساس است و در رویداد حسابرسی ثبت می‌شود.
            </span>
            <V2Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => onReopen(period)}
            >
              {busy ? 'در حال بازگشایی…' : `بازگشایی دورهٔ ${period.name}`}
            </V2Button>
          </>
        )}
      </div>
    </V2Card>
  );
}
